import { useMemo } from 'react'
import type { SegmentFilterQuestion } from '@/lib/utils/segment-filter-questions'
import {
  CATEGORY_LABELS,
  type SegmentationCategory,
} from '@/types/components/audiences/segmentation-mapping'

export interface UseSegmentFilterQuestionListOptions {
  questions: Array<SegmentFilterQuestion>
  search: string
}

export interface UseSegmentFilterQuestionListResult {
  visibleQuestions: Array<SegmentFilterQuestion>
  questionsByCategory: Array<[string, Array<SegmentFilterQuestion>]>
  categoryLabel: (category: string) => string
}

/**
 * Shared hook for searching and bucketing a SegmentFilterQuestion list by
 * category. Replaces duplicate memos across AddFilterModal, AddTagModal, and
 * future picker surfaces.
 */
export function useSegmentFilterQuestionList({
  questions,
  search,
}: UseSegmentFilterQuestionListOptions): UseSegmentFilterQuestionListResult {
  const visibleQuestions = useMemo(() => {
    const needle = search.trim().toLowerCase()
    if (!needle) return questions
    return questions.filter(
      (item) =>
        item.label.toLowerCase().includes(needle) ||
        item.options.some(
          (o) =>
            o.optionLabel.toLowerCase().includes(needle) ||
            o.sourceColumn.toLowerCase().includes(needle),
        ),
    )
  }, [questions, search])

  const questionsByCategory = useMemo(() => {
    const map = new Map<string, Array<SegmentFilterQuestion>>()
    for (const item of visibleQuestions) {
      if (!map.has(item.category)) map.set(item.category, [])
      map.get(item.category)!.push(item)
    }
    return Array.from(map.entries())
  }, [visibleQuestions])

  const categoryLabel = (category: string) =>
    CATEGORY_LABELS[category as SegmentationCategory] ?? 'Other'

  return { visibleQuestions, questionsByCategory, categoryLabel }
}
